import { useEffect, useState } from "react";

// Auto-rotating gallery carousel of Dr. Maggie's 24 personal photos.
// Displays as a single framed image that transitions every ~4s with a
// gentle crossfade + slight scale motion.
const IMAGES = Array.from({ length: 24 }, (_, i) => ({
  src: `/images/gallery/g${i + 1}.webp`,
  alt: `Dr. Maggie candid photo ${i + 1}, life beyond the clinic`,
}));

const INTERVAL_MS = 4000;

export default function AboutCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  // Only load the current slide and its neighbours (plus any already seen)
  const [loaded, setLoaded] = useState(() => new Set([0, 1, IMAGES.length - 1]));

  useEffect(() => {
    setLoaded((prev) => {
      const n = IMAGES.length;
      const want = [idx, (idx + 1) % n, (idx - 1 + n) % n];
      if (want.every((w) => prev.has(w))) return prev;
      const next = new Set(prev);
      want.forEach((w) => next.add(w));
      return next;
    });
  }, [idx]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % IMAGES.length);
    }, INTERVAL_MS);
    return () => clearInterval(t);
  }, [paused]);

  const goPrev = () => setIdx((i) => (i - 1 + IMAGES.length) % IMAGES.length);
  const goNext = () => setIdx((i) => (i + 1) % IMAGES.length);

  return (
    <div
      data-testid="about-carousel"
      className="relative rounded-[2rem] ring-1 ring-lavender/50 shadow-[0_30px_70px_-30px_rgba(75,31,99,0.4)] overflow-hidden bg-gradient-to-br from-lavender-bg via-white to-pale-blue/60 group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="Rotating gallery of Dr. Maggie beyond the clinic"
    >
      <div className="relative w-full h-[560px]">
        {IMAGES.map((img, i) => (
          <img
            key={i}
            src={loaded.has(i) ? img.src : undefined}
            alt={img.alt}
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            className={`absolute inset-0 w-full h-full object-contain transition-all duration-[1200ms] ease-out ${
              i === idx ? "opacity-100 scale-100" : "opacity-0 scale-[1.03]"
            }`}
            aria-hidden={i !== idx}
            data-testid={`about-carousel-slide-${i}`}
          />
        ))}

        {/* Manual navigation arrows */}
        <button
          type="button"
          data-testid="about-carousel-prev"
          onClick={goPrev}
          aria-label="Previous photo"
          className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/85 backdrop-blur text-plum flex items-center justify-center shadow-md ring-1 ring-lavender/60 opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:bg-white hover:scale-105 transition-all duration-300 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-royal"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          type="button"
          data-testid="about-carousel-next"
          onClick={goNext}
          aria-label="Next photo"
          className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/85 backdrop-blur text-plum flex items-center justify-center shadow-md ring-1 ring-lavender/60 opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:bg-white hover:scale-105 transition-all duration-300 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-royal"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        {/* Position dots */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5" aria-hidden="true">
          {IMAGES.map((_, i) => (
            <span
              key={i}
              data-testid={`about-carousel-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === idx
                  ? "w-6 bg-royal shadow-[0_0_8px_rgba(111,45,168,0.6)]"
                  : "w-1.5 bg-lavender/70"
              }`}
            />
          ))}
        </div>

        {/* Counter */}
        <div
          data-testid="about-carousel-counter"
          className="absolute top-4 right-4 bg-white/85 backdrop-blur px-3 py-1 rounded-full text-[10px] tracking-[0.24em] uppercase text-plum font-medium"
        >
          {String(idx + 1).padStart(2, "0")} / {IMAGES.length}
        </div>
      </div>
    </div>
  );
}
